import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { useData } from "../hooks/useData";

const SaveToBrowserDialog = ({ open, onClose, canvas, onSaveSuccess }) => {
  const { projectName, setProjectName } = useData();
  const [fileName, setFileName] = useState(projectName || "");
  const [isSaved, setIsSaved] = useState(false);
  const [fileNameError, setFileNameError] = useState("");
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef(null);

  // File name validation
  const maxFileNameLength = 50;
  const fileNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
  const validateFileName = (name) => {
    if (name.trim() === "") {
      return "File name is required.";
    }
    if (name.length > maxFileNameLength) {
      return `File name must be ${maxFileNameLength} characters or less.`;
    }
    if (!fileNameRegex.test(name)) {
      return "File name can only contain letters, numbers, spaces, hyphens, and underscores.";
    }
    return "";
  };

  const isSaveDisabled = fileName.trim() === "" || !!fileNameError;

  const handleFileNameChange = (e) => {
    const newName = e.target.value;
    setFileName(newName);
    setFileNameError(validateFileName(newName));
  };

  const handleSave = () => {
    const localStorageKey = `design_${fileName}`;
    if (localStorage.getItem(localStorageKey)) {
      setShowOverwriteDialog(true);
    } else {
      performSave(localStorageKey);
    }
  };

  const performSave = (localStorageKey) => {
    if (!canvas) return;
    try {
      const storageObj = canvas.toJSON();
      localStorage.setItem(localStorageKey, JSON.stringify(storageObj));
      setProjectName(fileName);
      setIsSaved(true);
      onSaveSuccess?.();
    } catch (e) {
      console.error("Error saving to local storage:", e);
    }
  };

  const handleOverwriteConfirm = () => {
    setShowOverwriteDialog(false);
    performSave(`design_${fileName}`);
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteDialog(false);
  };

  const handleClose = () => {
    setFileName(projectName || "");
    setFileNameError("");
    setIsSaved(false);
    setShowOverwriteDialog(false);
    setDialogPosition({ x: 0, y: 0 });
    onClose();
  };

  // Draggable functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - dialogPosition.x,
      y: e.clientY - dialogPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 400,
            transform: `translate(${dialogPosition.x}px, ${dialogPosition.y}px)`,
          },
        }}
        aria-labelledby="dialog-title"
        maxWidth="sm"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <DialogTitle
          id="dialog-title"
          sx={{
            bgcolor: "primary.main",
            color: "white",
            fontSize: "1.25rem",
            cursor: "move",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            pb: 2,
          }}
          onMouseDown={handleMouseDown}
        >
          <DragIndicatorOutlinedIcon sx={{ fontSize: 20, mr: 1 }} />
          Save to Browser Storage
        </DialogTitle>
        <DialogContent sx={{ mt: 4, mb: 4, pt: 4, pb: 4 }}>
          {!isSaved ? (
            <Stack direction="column" spacing={3}>
              <TextField
                id="fileName"
                label="File Name"
                value={fileName}
                onChange={handleFileNameChange}
                variant="outlined"
                size="medium"
                required
                fullWidth
                sx={{
                  maxWidth: 350,
                  mt: 2,
                  pt: 1,
                  "& .MuiInputLabel-root": {
                    fontSize: "1rem",
                    pt: 1,
                  },
                }}
                error={!!fileNameError}
                helperText={fileNameError || "Enter a name for your design"}
              />
              <DialogActions sx={{ justifyContent: "flex-end", px: 0 }}>
                <Button onClick={handleClose} color="secondary">
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  variant="contained"
                  color="primary"
                >
                  Save
                </Button>
              </DialogActions>
            </Stack>
          ) : (
            <Stack direction="column" spacing={2} alignItems="center">
              <Typography variant="h6" color="success.main">
                Success
              </Typography>
              <Typography variant="body1">
                Your design has been saved to browser local storage.
              </Typography>
              <Button
                onClick={handleClose}
                variant="contained"
                color="success"
                sx={{ mt: 1 }}
              >
                Close
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Overwrite Confirmation Dialog */}
      <Dialog
        open={showOverwriteDialog}
        onClose={handleOverwriteCancel}
        aria-labelledby="overwrite-dialog-title"
        maxWidth="xs"
      >
        <DialogTitle id="overwrite-dialog-title">Confirm Overwrite</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            A design with the name "{fileName}" already exists. Do you want to
            overwrite it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverwriteCancel} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleOverwriteConfirm}
            variant="contained"
            color="primary"
          >
            Overwrite
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaveToBrowserDialog;
